import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Button, Card, CenteredLoader, Table } from '@/components_deprecated'
import { PermissionsRequired } from '@/containers/permission-containers'
import { formatISO } from 'date-fns'
import uniq from 'lodash/uniq'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import type { AllIssueSubcategories, ExportIssueForm } from '@/modules/issues'
import {
  ALL_ISSUE_RISK_ASPECTS_QUERY,
  ExportIssuePrompt,
  GET_ALL_ISSUE_SUBCATEGORIES,
} from '@/modules/issues'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import type { IdVariable } from '@/types/graphql'
import type { Coordinates } from '@/types/graphql/maps'
import type { Nullable } from '@/types/util'
import { format } from '@/utils/date-fns'
import type { FileAttachment } from '@/utils/hooks'
import {
  useBreadcrumbs,
  useModuleConfig,
  useOnErrorAuto,
  useParam,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { EditIssueForm } from '..'
import { ISSUE_QUERY } from '..'
import type { AttachmentsRef } from '../components'
import { Attachments, Details, MainInfo, Map } from '../components'
import {
  useExportIssuesMutations,
  usePatchIssueMutation,
} from '../mutations.hooks'
import type {
  AllIssueRiskAspectsQuery,
  AllIssueRiskAspectsQueryVariables,
  IssueQuery,
  PatchIssueInput,
} from '../types.graphl'
import { H2 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  header {
    display: flex;
    justify-content: space-between;
  }
`

const ActionLogTable = styled(Table)`
  margin-top: 1rem;
`

const Buttons = styled.div`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  justify-content: ${isMobile ? 'space-between' : 'flex-end'};

  button {
    height: 3rem;

    & + button {
      margin-left: 1rem;
    }
  }
`
const Content = styled(Card)`
  position: relative;
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-areas:
    'main        map'
    'details     map'
    'attachments map';

  padding: 0;

  ${props => props.theme.media.custom({ max: '1500px' })} {
    display: flex;
    flex-direction: column;
  }
`
const Loading = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background-color: rgba(255, 255, 255, 0.5);
`

interface IssueProps {}

export const Issue: React.FC<IssueProps> = () => {
  const translations = useTranslate({
    issue: 'common.issue',
    loading: 'common.loading...',
    edit: 'common.edit',
    cancel: 'common.cancel',
    save: 'common.save',

    open: 'common.open-alt',
    closed: 'common.closed',
    archived: ['common.archived', val => val.toLowerCase()],

    status: 'common.status',
    category: 'common.category',
    responsible: 'common.responsible',
    date: 'common.date',
    reference: ['issues.reference', { abbreviate: false }],
    regNr: 'common.reg-nr',
    about: 'common.about',
    action: 'common.action',
    timestamp: 'common.timestamp',
    actionLogTitle: 'issues.action-log',

    export: 'common.export',

    validation: {
      solutionRequired: 'issues.validation.solution-required',
      required: 'common.required',
    },
    actionLog: {
      CREATED: ['issues.created-by-user', { user: 'user' }],
      UPDATED: ['issues.updated-by-user', { user: 'user' }],

      ARCHIVED: ['issues.archived-by-user', { user: 'user' }],
      RESTORED: ['issues.restored-by-user', { user: 'user' }],

      CLOSED: ['issues.closed-by-user', { user: 'user' }],
      REOPENED: ['issues.reopened-by-user', { user: 'user' }],

      REMINDER: [
        'issues.reminder-by-user-to-user',
        { user: 'user', target: 'target' },
      ],
    },
  })

  const issueId = useParam('issueId')

  const attachmentsRef = useRef<AttachmentsRef>(null)

  const location = useLocation()

  const me = useUser()
  const { setOverride } = useBreadcrumbs()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const { getModuleOptionStatus } = useModuleConfig(Module.ISSUES)
  const requireSeverity = getModuleOptionStatus(
    ModuleOptionsValue.ISSUES_REQUIRE_SEVERITY
  )

  const [editing, setEditing] = useState(!!location.state?.edit)
  const [coordinates, setCoordinates] = useState<Nullable<Coordinates>>({
    lat: null,
    lng: null,
  })
  const [attachmentsAdd, setAttachmentsAdd] = useState<
    Omit<FileAttachment, 'id'>[]
  >([])
  const [attachmentsRemove, setattachmentsRemove] = useState<string[]>([])

  const validateSolution = (
    solution: string | undefined,
    form: EditIssueForm
  ) => {
    if (form.closed && solution?.trim() === '')
      return translations.validation.solutionRequired
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formEdited,
    resetForm,
    updateForm,
    updateInitialValues,
    formChangeHandler: handler,
    submitHandler: submit,
    validateForm,
  } = useForm<EditIssueForm>({
    values: {
      user: '',
      responsibleUser: '',
      closed: false,
      archived: false,
      category: '',
      date: new Date(),
      dateEnd: new Date(),

      placename: '',
      regNr: '',
      description: '',
      damage: '',
      locationDescription: '',

      estimatedCost: 0,
      actualCost: 0,
      suggestedSolution: '',
      solution: '',
      severity: null,
      riskAspect: null,
      forInfringements: false,
      subcategory: '',
    },
    validators: {
      subcategory: val =>
        !!val || !hasSubcategories ? null : translations.validation.required,
      solution: validateSolution,
      category: val => (val ? null : translations.validation.required),
      placename: (val, other) =>
        !!val || other.forInfringements
          ? null
          : translations.validation.required,
      description: val => (val ? null : translations.validation.required),
      damage: (val, other) =>
        !!val || other.forInfringements
          ? null
          : translations.validation.required,
      suggestedSolution: (val, other) =>
        !!val || other.forInfringements
          ? null
          : translations.validation.required,
      riskAspect: (val, other) =>
        !hasRiskAspects || !!val || other.forInfringements
          ? null
          : translations.validation.required,
      severity: val =>
        !!val || !requireSeverity ? null : translations.validation.required,
    },
    config: {
      onlyValidateAfterFirstUpdate: false,
    },
  })

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<IssueQuery, IdVariable>(ISSUE_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: issueId,
    },
    onCompleted: handleData,
    onError: onErrorAuto(),
  })

  const { data: subcategories, loading: subcategoriesLoading } = useQuery<
    AllIssueSubcategories,
    { categoryId: string }
  >(GET_ALL_ISSUE_SUBCATEGORIES, {
    variables: { categoryId: form.category ?? '' },
    skip: !form.category,
    fetchPolicy: 'network-only',
    onError: onErrorAuto(),
  })

  const hasSubcategories: boolean =
    (subcategories?.allIssueSubcategories?.edges?.length ?? 0) > 0

  useEffect(() => {
    validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubcategories])

  function handleData(data: IssueQuery | undefined) {
    if (!data) return

    const { issue } = data
    const reference = issue.referenceId
    const date = format(new Date(issue.timeOfIssueStart), 'PP')

    setOverride(issue.id, reference ? `${reference} (${date})` : date)

    setCoordinates({
      lat: issue.locationLatitude,
      lng: issue.locationLongitude,
    })

    const values: EditIssueForm = {
      user: issue.user?.id ?? null,
      responsibleUser: issue.responsibleUser?.id ?? null,
      closed: issue.closedAt !== null,
      archived: issue.archivedAt !== null,
      category: issue.category?.id ?? null,
      subcategory: issue.subcategory?.id ?? null,
      date: new Date(issue.timeOfIssueStart),
      dateEnd: issue.timeOfIssueEnd
        ? new Date(issue.timeOfIssueEnd)
        : new Date(),

      placename: issue.locationPlacename,
      regNr: issue.vehiclePlateNumber ?? '',
      description: issue.issueDescription,
      damage: issue.damageDescription,
      locationDescription: issue.locationDescription,

      estimatedCost: issue.costEstimation,
      suggestedSolution: issue.solutionSuggestion,
      actualCost: issue.actualCost,
      solution: issue.solution,
      severity: issue.severity,
      riskAspect: issue.riskAspect?.id ?? null,
      forInfringements: issue.category.forInfringements ?? false,
    }
    updateForm(values)
    updateInitialValues(values)
  }

  const { data: riskAspectData } = useQuery<
    AllIssueRiskAspectsQuery,
    AllIssueRiskAspectsQueryVariables
  >(ALL_ISSUE_RISK_ASPECTS_QUERY)

  const hasRiskAspects =
    (riskAspectData?.allIssueRiskAspects.edges.length ?? 0) > 0

  const mutations = usePatchIssueMutation()
  const { exportIssue, loading: emailLoading } = useExportIssuesMutations()

  useEffect(() => {
    if (data) return
    setOverride(issueId, translations.loading)
  })

  const issue = useMemo(() => data?.issue, [data])
  const attachments = useMemo(() => issue?.attachments ?? [], [issue])

  useEffect(() => {
    if (!issue) return

    if (!editing) {
      setCoordinates({
        lat: issue.locationLatitude,
        lng: issue.locationLongitude,
      })
    }
  }, [editing, issue])

  function handleLocationUpdate(
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) {
    if (!editing || status !== google.maps.GeocoderStatus.OK || !results) return
    const [place] = results
    if (!place || !place.formatted_address) return

    const [lat, lng] = [
      place.geometry.location.lat(),
      place.geometry.location.lng(),
    ]
    setCoordinates({
      lat,
      lng,
    })

    updateForm({
      placename: place.formatted_address,
    })
  }
  function handleAttachmentsUpdate(add: FileAttachment[], remove: string[]) {
    setAttachmentsAdd(
      add.map(attachment => ({
        name: attachment.name,
        file: attachment.file,
      }))
    )
    setattachmentsRemove(remove)
  }

  async function doExport(values: ExportIssueForm) {
    if (!issue) return
    await exportIssue({
      variables: {
        userId: me.id,
        reportId: issue?.id,
        recipients: uniq(values.recipients),
        exportFormat: values.exportFormat,
        exportType: values.exportFormat,
        exportOption: values.exportOption,
      },
    })
  }

  async function handleExport() {
    if (!issue) return

    const { data } = await addPrompt<ExportIssueForm | null>(resolve => (
      <ExportIssuePrompt onSubmit={resolve} user={me} />
    ))
    if (!data) return
    await doExport(data)
  }

  function handleCancel() {
    resetForm()
    setEditing(false)

    if (!attachmentsRef.current) return
    attachmentsRef.current.resetEditedAttachments()
  }

  async function refreshIssue() {
    const { data: newData } = await refetch()
    handleData(newData)
  }

  function handleSubmit(values: EditIssueForm) {
    if (!issue || !values.category) return

    if (typeof google === 'undefined')
      throw new Error('Issue submit: google is undefined')

    async function runMutation(input: PatchIssueInput) {
      if (!issue) return

      try {
        await mutations.patch({
          variables: { id: issue.id, input },
        })
        await refreshIssue()
        handleCancel()
      } catch (error) {
        console.error('Issue submit error:', error)
      }
    }

    let input: PatchIssueInput = {
      responsibleUser: values.responsibleUser,
      category: values.category,
      subcategory: values.subcategory,
      timeOfIssueStart: formatISO(values.date),
      timeOfIssueEnd: formatISO(values.dateEnd),

      locationPlacename: values.placename,
      locationDescription: values.locationDescription,

      vehiclePlateNumber: values.regNr,
      issueDescription: values.description,
      damageDescription: values.damage,

      costEstimation: values.estimatedCost,
      solutionSuggestion: values.suggestedSolution,
      actualCost: values.actualCost,
      solution: values.solution ?? '',

      attachmentsAdd,
      attachmentsRemove,
      severity: values.severity,
      riskAspect: values.riskAspect,
    }
    if (issue.closedAt === null && values.closed)
      input = {
        ...input,
        closedAt: formatISO(new Date()),
        closedBy: me.id,
      }
    else if (!values.closed)
      input = {
        ...input,
        closedAt: null,
        closedBy: null,
      }

    if (coordinates.lat === null || coordinates.lng === null) {
      const service = new google.maps.Geocoder()
      void service.geocode(
        {
          address: values.placename,
        },
        (results, status) => {
          const [place] = results ?? [null]
          if (status !== google.maps.GeocoderStatus.OK || !place) {
            void runMutation(input)
            return
          }

          const [lat, lng] = [
            place.geometry.location.lat(),
            place.geometry.location.lng(),
          ]
          input = {
            ...input,
            locationLatitude: lat,
            locationLongitude: lng,
          }
          void runMutation(input)
        }
      )
    } else {
      input = {
        ...input,
        locationLatitude: coordinates.lat,
        locationLongitude: coordinates.lng,
      }
      void runMutation(input)
    }
  }

  const isLoading = queryLoading || mutations.loading || emailLoading
  const isFormEdited = useMemo(
    () => formEdited || !!attachmentsAdd.length || !!attachmentsRemove.length,
    [formEdited, attachmentsAdd.length, attachmentsRemove.length]
  )

  const editPermission = issue?.category?.forInfringements
    ? []
    : PERMISSIONS.issues.change.issue

  return (
    <Wrapper>
      <header className="mb-4">
        <H2>{translations.issue}</H2>
      </header>

      <Content>
        {isLoading && (
          <Loading>
            <CenteredLoader marginTop="0" size={72} thickness={8} />
          </Loading>
        )}

        <Map
          editing={editing && !form.forInfringements}
          latitude={coordinates.lat}
          longitude={coordinates.lng}
          onClick={handleLocationUpdate}
        />

        <MainInfo
          subcategoryOptions={(
            subcategories?.allIssueSubcategories?.edges ?? []
          )?.map(el => ({
            label: el?.node?.name,
            value: el?.node?.id,
          }))}
          subcategoriesLoading={subcategoriesLoading}
          loading={queryLoading}
          editing={editing}
          form={form}
          errors={errors}
          subCategory={issue?.subcategory}
          category={issue?.category}
          date={issue?.timeOfIssueStart}
          closedAt={issue?.closedAt}
          archivedAt={issue?.archivedAt}
          reference={issue?.referenceId}
          user={issue?.user}
          responsible={issue?.responsibleUser}
          severity={issue?.severity}
          riskAspect={issue?.riskAspect}
          dateEnd={issue?.timeOfIssueEnd}
          onChange={handler}
          updateForm={updateForm}
        />

        <Details
          loading={queryLoading}
          editing={editing}
          form={form}
          errors={errors}
          placename={issue?.locationPlacename}
          regNr={issue?.vehiclePlateNumber}
          damage={issue?.damageDescription}
          description={issue?.issueDescription}
          locationDescription={issue?.locationDescription}
          costEstimate={issue?.costEstimation}
          actualCost={issue?.actualCost}
          suggestedSolution={issue?.solutionSuggestion}
          solution={issue?.solution}
          relatedLink={issue?.relatedLink}
          onLocationUpdate={handleLocationUpdate}
          onChange={handler}
        />

        <Attachments
          ref={attachmentsRef}
          editing={editing}
          attachments={attachments}
          onUpdate={handleAttachmentsUpdate}
        />
      </Content>
      <Buttons>
        {!editing && (
          <PermissionsRequired
            permissions={PERMISSIONS.companies.export.exportReports}
          >
            <Button
              disabled={isLoading || !issue}
              padding="0 2rem"
              onClick={handleExport}
            >
              <Icon icon="inbox-in" fixedWidth margin="0 6px 0 0" />
              {translations.export}
            </Button>
          </PermissionsRequired>
        )}

        {editing && (
          <Button
            disabled={isLoading}
            background="gray8"
            onClick={handleCancel}
          >
            {translations.cancel}
          </Button>
        )}
        <PermissionsRequired permissions={editPermission}>
          <Button
            disabled={
              isLoading ||
              (editing && (!formValid || !isFormEdited)) ||
              issue?.closedAt !== null
            }
            onClick={editing ? submit(handleSubmit) : () => setEditing(true)}
          >
            {editing ? translations.save : translations.edit}
          </Button>
        </PermissionsRequired>
      </Buttons>

      {(issue?.actionLogs?.edges?.length ?? 0) > 0 && (
        <>
          <h2>{translations.actionLogTitle}</h2>
          <ActionLogTable>
            <thead>
              <tr>
                <th>{translations.timestamp}</th>
                <th>{translations.action}</th>
              </tr>
            </thead>

            <tbody>
              {issue?.actionLogs.edges.map(({ node }) => {
                const createdAt = format(new Date(node.createdAt), 'PPpp')
                const user = node.user ? node.user.fullName : 'System'
                const secondaryUser = node.secondaryUser
                  ? node.secondaryUser.fullName
                  : ''
                const actionString = node.secondaryUser
                  ? translations.actionLog.REMINDER({
                      user: user,
                      target: secondaryUser,
                    })
                  : translations.actionLog[node.action]({ user: user })

                return (
                  <tr key={node.id}>
                    <td>{createdAt}</td>
                    <td>{actionString}</td>
                  </tr>
                )
              })}
            </tbody>
          </ActionLogTable>
        </>
      )}
    </Wrapper>
  )
}
